import { ReactNode } from 'react';

import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import { MetadataOptionsQuestion } from '../../types';
import { normalizeMeterConfigurations } from '../../utils/meterConfigurations';

const customStyles = {
  highlight: { fontWeight: 500 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Warning body for the delete-question dialog.
 *
 * Deleting a question that feeds an asset meter takes more with it than the question: the
 * meter configuration on the asset question keeps a slot pointing at an id that no longer
 * exists, and any validation configured against that meter stops running. The generic copy
 * ("this question will be permanently deleted") does not say that, so an author deleting what
 * looks like a spare number question gets no warning that they are unwiring a meter.
 *
 * Returns the plain message when there is nothing extra at stake, so the common case reads
 * exactly as it does today.
 */
export function deleteQuestionWarningMessage(
  hasAssetLinks: boolean,
  copy: { questionAlert: string; withAssetLinksPrefix: string; withAssetLinksHighlight: string },
): ReactNode {
  if (!hasAssetLinks) return copy.questionAlert;

  return (
    <Box component='span'>
      {copy.withAssetLinksPrefix}
      <Box
        component='span'
        sx={customStyles.highlight}
      >
        {copy.withAssetLinksHighlight}
      </Box>
    </Box>
  );
}

/** What this check reads. Nesting matters because a group carries no links of its own. */
interface DeletionCandidate {
  associatedMetadata?: unknown;
  questions?: unknown[];
}

/**
 * A question is "linked to asset data" when it carries the metadata that ties it to a meter or
 * to location updating. This is deliberately looser than the meter-link predicate in
 * `utils/meterLink`: here we only ask whether deleting it could break wiring elsewhere, so a
 * half-broken link still deserves the warning.
 *
 * Two things make it looser in practice and not only in intent:
 *
 * - Meter configurations are read through `normalizeMeterConfigurations`, so a question saved
 *   before that array existed (a bare `meterId`, or `updateData: '1'`) still warns. Reading the
 *   raw field made this check STRICTER than the predicate for exactly those questions, which is
 *   the opposite of what the paragraph above promises.
 * - It recurses one level into a repeatable group. The group itself carries no
 *   `associatedMetadata` and no meter configuration; its children do. Deleting the group takes
 *   every link inside it, and without the recursion that deletion showed the generic copy.
 */
export function hasAssetDataLinks(question: DeletionCandidate | undefined): boolean {
  // Both ends of the link deserve the warning, and the asset end is the more damaging one:
  // deleting the asset question leaves every number question that fed its meters holding a rule
  // that can never run again. Keying only on `associatedMetadata` would give that deletion the
  // generic copy, because the metadata lives on the number question.
  if (question?.associatedMetadata != null) return true;
  if (normalizeMeterConfigurations((question ?? {}) as MetadataOptionsQuestion).length > 0) {
    return true;
  }

  return (question?.questions ?? []).some((child) => hasAssetDataLinks(child as DeletionCandidate));
}
